import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  accordionHeaderClassNames,
  Button,
  Drawer,
  makeStyles,
  mergeClasses,
  Text,
  tokens,
  Tooltip,
} from "@fluentui/react-components";
import {
  ChevronLeftRegular,
  ChevronRightRegular,
  DataBarVerticalRegular,
  DataPieRegular,
  DesktopRegular,
  BookOpenRegular,
  BuildingRegular,
  HeadsetRegular,
  HeartPulseRegular,
  HourglassHalfRegular,
  LayerDiagonalRegular,
  LinkRegular,
  NetworkCheckRegular,
  ReceiptRegular,
  ServerRegular,
  SettingsRegular,
  ShieldRegular,
  TableColumnTopBottomRegular,
  TentRegular,
  ArrowTrendingLinesRegular,
  BoxEditRegular,
  CardUiRegular,
  PeopleTeamRegular,
} from "@fluentui/react-icons";

import { useSignInState } from "../Components/signInState";
import { useConfig } from "../context/ConfigContext";
import { isBasecampWorkspaceAdminTier } from "../constants/basecampWorkspaceRoleIds";
import { WorkspaceFeatureIds } from "../constants/workspaceFeatureIds";
import { bffRequestHeaders } from "../services/bffFetch";
import { hasActiveWorkspaceFeature } from "../services/workspaceSubscription";
import { buildBasecampBffLoginUrl } from "../utils/buildBasecampBffLoginUrl";

interface IdentityNavigationLink {
  label: string;
  url: string;
  target: string;
  status: number;
  icon?: string;
}

const identityNavIconsByName: Record<string, JSX.Element> = {
  HourglassHalfRegular: <HourglassHalfRegular />,
  ReceiptRegular: <ReceiptRegular />,
  DataBarVerticalRegular: <DataBarVerticalRegular />,
  DataPieRegular: <DataPieRegular />,
  LayerDiagonalRegular: <LayerDiagonalRegular />,
  TableColumnTopBottomRegular: <TableColumnTopBottomRegular />,
  DesktopRegular: <DesktopRegular />,
  TentRegular: <TentRegular />,
  HeadsetRegular: <HeadsetRegular />,
  NetworkCheckRegular: <NetworkCheckRegular />,
  ServerRegular: <ServerRegular />,
  HeartPulseRegular: <HeartPulseRegular />,
  ShieldRegular: <ShieldRegular />,
};

const productSwitcherIcon = (iconName: string | undefined) => {
  const normalizedIconName = iconName?.trim() ?? "";
  return identityNavIconsByName[normalizedIconName] ?? <LinkRegular />;
};

interface NavItemProps {
  label: string;
  icon: any;
  onClick: () => void;
  disabled?: boolean;
  isExpanded?: boolean;
  itemId?: string;
  href?: string;
  isSubItem?: boolean;
  clickedItem?: string | null;
  styles: any;
}

const NavItem = ({
  label,
  icon,
  onClick,
  disabled,
  isExpanded,
  itemId,
  href,
  isSubItem = false,
  clickedItem,
  styles,
}: NavItemProps) => {
  const location = useLocation();

  const isActiveRoute = (href: string) => {
    if (href === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(href);
  };

  const isActive = href ? isActiveRoute(href) : false;
  const isClicked = clickedItem === itemId;

  let className = styles.navItemDefault;
  if (isClicked) {
    className = styles.navItemClicked;
  } else if (isActive) {
    className = isSubItem ? styles.navItemSubActive : styles.navItemActive;
  }

  return (
    <AccordionItem
      value={label}
      disabled={disabled}
      onClick={onClick}
      className={className}
    >
      {isExpanded ? (
        <AccordionHeader icon={icon} expandIcon={null}>
          {label}
        </AccordionHeader>
      ) : (
        <Tooltip content={label} relationship="label">
          <AccordionHeader icon={icon} expandIcon={null} />
        </Tooltip>
      )}
    </AccordionItem>
  );
};

const useStyles = makeStyles({
  navRoot: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    width: "100%",
    minWidth: "100%",
    boxSizing: "border-box",
  },
  navContentInset: {
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
  },
  navContentInsetCollapsed: {
    paddingLeft: tokens.spacingHorizontalS,
    paddingRight: tokens.spacingHorizontalS,
  },
  collapseToolbar: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
    flexShrink: 0,
    width: "100%",
    boxSizing: "border-box",
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalXS,
  },
  collapseButton: {
    minWidth: "28px",
    minHeight: "28px",
  },
  drawerContent: {
    display: "flex",
    flexDirection: "column",
    gap: 0,
    flex: 1,
    minHeight: 0,
    overflow: "hidden",
  },
  navScrollMain: {
    display: "flex",
    flexDirection: "column",
    flex: "1 1 auto",
    minHeight: 0,
    overflowY: "auto",
  },
  navFooterSection: {
    display: "flex",
    flexDirection: "column",
    flexShrink: 0,
    marginTop: "auto",
  },
  sectionSeparator: {
    flexShrink: 0,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    marginTop: tokens.spacingVerticalXXS,
    marginBottom: tokens.spacingVerticalXXS,
  },
  navAccordion: {
    width: "100%",
    [`& .${accordionHeaderClassNames.button}`]: {
      paddingLeft: tokens.spacingHorizontalXXS,
      paddingRight: tokens.spacingHorizontalS,
      columnGap: tokens.spacingHorizontalXS,
    },
  },
  navItemClicked: {
    backgroundColor: tokens.colorNeutralBackground1Pressed,
    transform: "scale(0.98)",
    transition: "all 150ms ease-out",
    "& > div": {
      backgroundColor: tokens.colorNeutralBackground1Pressed,
    },
  },
  navItemDefault: {
    transition: "all 150ms ease-out",
    position: "relative",
    "&:hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  navItemActive: {
    transition: "all 150ms ease-out",
    position: "relative",
    backgroundColor: tokens.colorNeutralBackground2,

    "&::before": {
      content: '""',
      position: "absolute",
      left: "0px",
      top: "50%",
      transform: "translateY(-50%)",
      width: "3px",
      height: "60%",
      backgroundColor: tokens.colorCompoundBrandBackground,
    },

    [`& .${accordionHeaderClassNames.button}`]: {
      paddingLeft: "10px",
      fontWeight: tokens.fontWeightSemibold,
      color: tokens.colorCompoundBrandForeground1,

      "& > span:first-child": {
        color: tokens.colorCompoundBrandForeground1,
      },
    },

    "&:hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  sectionTitle: {
    color: tokens.colorNeutralForeground3,
    display: "block",
    margin: 0,
    fontSize: "14px",
    fontWeight: 800,
    paddingTop: "5px",
    paddingLeft: tokens.spacingHorizontalXXS,
    paddingRight: 0,
    textAlign: "left",
  },
  sectionBlock: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXXS,
  },
  dashboardLinksBlock: {
    padding: "10px",
  },
  navItemSubActive: {
    transition: "all 150ms ease-out",
    position: "relative",
    marginLeft: "8px",
    backgroundColor: tokens.colorNeutralBackground2,

    "&::before": {
      content: '""',
      position: "absolute",
      left: "0px",
      top: "50%",
      transform: "translateY(-50%)",
      width: "3px",
      height: "60%",
      backgroundColor: tokens.colorCompoundBrandBackground,
    },

    [`& .${accordionHeaderClassNames.button}`]: {
      paddingLeft: "10px",
      fontWeight: tokens.fontWeightMedium,
      color: tokens.colorBrandForeground1,

      "& > span:first-child": {
        color: tokens.colorBrandForeground1,
      },
    },

    "&:hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
});

const SideNav = () => {
  const styles = useStyles();
  const { guidesUrl, basecampUrl } = useConfig();
  const navigate = useNavigate();
  const location = useLocation();
  const { signInState } = useSignInState();
  const [isExpanded, setIsExpanded] = useState(true);
  const [clickedItem, setClickedItem] = useState<string | null>(null);
  const [productSwitcherLinks, setProductSwitcherLinks] = useState<
    IdentityNavigationLink[]
  >([]);
  const openedExternalWindowsRef = useRef<Record<string, Window | null>>({});

  useEffect(() => {
    if (!signInState.isAuthed || !signInState.userInfo) {
      setProductSwitcherLinks([]);
      return;
    }

    const controller = new AbortController();

    const loadLinks = async () => {
      try {
        const response = await fetch(
          "/bff/api/navigation/links?productName=Clarity360",
          {
            method: "GET",
            credentials: "include",
            headers: bffRequestHeaders(signInState.currentCustomerID),
            signal: controller.signal,
          }
        );
        if (!response.ok) {
          setProductSwitcherLinks([]);
          return;
        }
        const data = (await response.json()) as IdentityNavigationLink[];
        if (!Array.isArray(data)) {
          setProductSwitcherLinks([]);
          return;
        }
        const filtered = data.filter(
          (link) =>
            link.status === 1 &&
            String(link.label ?? "")
              .trim()
              .toLowerCase() !== "clarity"
        );
        setProductSwitcherLinks(filtered);
      } catch {
        if (!controller.signal.aborted)
          setProductSwitcherLinks([]);
      }
    };

    void loadLinks();
    return () => controller.abort();
  }, [
    signInState.isAuthed,
    signInState.currentCustomerID,
    signInState.userInfo,
  ]);

  const toggleDrawer = () => {
    setIsExpanded(!isExpanded);
  };

  const handleNavItemClick = useCallback(
    (href: string, itemId: string) => {
      // Provide visual feedback
      setClickedItem(itemId);

      // Clear the clicked state after animation
      setTimeout(() => {
        setClickedItem(null);
      }, 200);

      // Check if we're clicking on the exact current route.
      // For non-root routes we compare exact path so that clicking a base
      // nav item (e.g. '/intunedashboards') while on a sub-path
      // (e.g. '/intunedashboards/bad-url') will navigate back instead of reload.
      const isCurrentRoute =
        href === "/"
          ? location.pathname === "/"
          : location.pathname === href;

      if (isCurrentRoute) {
        // Reload the page when clicking on the active route
        globalThis.location.reload();
      } else {
        // Navigate to different route
        navigate(href);
      }
    },
    [navigate, location.pathname]
  );

  const resolveExternalNavTarget = useCallback(
    (rawTarget: string | undefined, uniqueKey: string) => {
      const trimmedTarget = rawTarget?.trim() ?? "";
      const normalizedTarget = trimmedTarget.toLowerCase();

      if (!trimmedTarget || normalizedTarget === "_blank") {
        const sanitizedKey = uniqueKey
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-+|-+$/g, "");
        return `clarity-ext-${sanitizedKey || "link"}`;
      }

      return trimmedTarget;
    },
    []
  );

  const openExternalNavLink = useCallback(
    (url: string, rawTarget: string | undefined, uniqueKey: string) => {
      const target = resolveExternalNavTarget(rawTarget, uniqueKey);
      const existingWindow = openedExternalWindowsRef.current[target];
      if (existingWindow && !existingWindow.closed) {
        existingWindow.focus();
        return;
      }

      const openedWindow = globalThis.open(url, target);
      if (openedWindow) {
        openedExternalWindowsRef.current[target] = openedWindow;
        openedWindow.focus();
      }
    },
    [resolveExternalNavTarget]
  );

  const sub = signInState.workspaceSubscription;
  const bypassWorkspaceGuards = signInState.isWorkspaceSuperAdmin;

  const hasOutOfTheBoxReportsAccess = useCallback(
    () =>
      hasActiveWorkspaceFeature(
        sub,
        WorkspaceFeatureIds.ClarityOutOfBoxReports,
        bypassWorkspaceGuards,
      ),
    [sub, bypassWorkspaceGuards],
  );

  const hasCustomReportsAccess = useCallback(
    () =>
      hasActiveWorkspaceFeature(
        sub,
        WorkspaceFeatureIds.ClarityCustomReports,
        bypassWorkspaceGuards,
      ),
    [sub, bypassWorkspaceGuards],
  );

  const checkAdmin = useCallback((): boolean => {
    return isBasecampWorkspaceAdminTier(bypassWorkspaceGuards, signInState.workspaceRoleId);
  }, [bypassWorkspaceGuards, signInState.workspaceRoleId]);

  const navInset = mergeClasses(
    styles.navContentInset,
    !isExpanded && styles.navContentInsetCollapsed
  );

  return (
    <Drawer
      open={true}
      style={{
        width: isExpanded ? "240px" : "70px",
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
      size="small"
      position="start"
      type="inline"
      separator
    >
      <div className={styles.navRoot}>
        <div className={mergeClasses(styles.collapseToolbar, navInset)}>
          <Tooltip
            content={isExpanded ? "Collapse sidebar" : "Expand sidebar"}
            relationship="label"
          >
            <Button
              appearance="subtle"
              className={styles.collapseButton}
              icon={
                isExpanded ? (
                  <ChevronLeftRegular />
                ) : (
                  <ChevronRightRegular />
                )
              }
              onClick={toggleDrawer}
            />
          </Tooltip>
        </div>
        <div className={mergeClasses(styles.drawerContent, navInset)}>
          <div className={styles.navScrollMain}>
          {isExpanded && (
            <Text size={200} weight="semibold" className={styles.sectionTitle}>
              Dashboards
            </Text>
          )}
          <div
            className={mergeClasses(
              styles.sectionBlock,
              isExpanded && styles.dashboardLinksBlock
            )}
          >
            <Accordion
              collapsible
              multiple
              className={styles.navAccordion}
            >
              <NavItem
                label="Intune Dashboards"
                icon={<ArrowTrendingLinesRegular />}
                onClick={() =>
                  handleNavItemClick("/intunedashboards", "intune-dashboards")
                }
                disabled={!hasOutOfTheBoxReportsAccess()}
                isExpanded={isExpanded}
                itemId="intune-dashboards"
                href="/intunedashboards"
                clickedItem={clickedItem}
                styles={styles}
              />
              <NavItem
                label="Custom Dashboards"
                icon={<CardUiRegular />}
                onClick={() =>
                  handleNavItemClick("/customdashboards", "custom-dashboards")
                }
                disabled={!hasCustomReportsAccess()}
                isExpanded={isExpanded}
                itemId="custom-dashboards"
                href="/customdashboards"
                clickedItem={clickedItem}
                styles={styles}
              />
              <NavItem
                label="Create Dashboards"
                icon={<BoxEditRegular />}
                onClick={() =>
                  handleNavItemClick("/createdash", "create-dashboards")
                }
                disabled={!hasCustomReportsAccess()}
                isExpanded={isExpanded}
                itemId="create-dashboards"
                href="/createdash"
                clickedItem={clickedItem}
                styles={styles}
              />
            </Accordion>
          </div>

          <div
            className={styles.sectionSeparator}
            role="separator"
            aria-hidden="true"
          />

          <div className={styles.sectionBlock}>
            <Accordion
              collapsible
              multiple
              className={styles.navAccordion}
            >
              {productSwitcherLinks.map((link) => {
                const itemId = `ext-nav-${link.label}-${link.url}`.replace(
                  /\s+/g,
                  "-"
                );
                return (
                  <NavItem
                    key={itemId}
                    label={link.label}
                    icon={productSwitcherIcon(link.icon)}
                    onClick={() => {
                      setClickedItem(itemId);
                      setTimeout(() => setClickedItem(null), 200);
                      openExternalNavLink(
                        link.url,
                        link.target,
                        `${link.label}-${link.url}`
                      );
                    }}
                    isExpanded={isExpanded}
                    itemId={itemId}
                    clickedItem={clickedItem}
                    styles={styles}
                  />
                );
              })}
            </Accordion>
          </div>

          <div
            className={styles.sectionSeparator}
            role="separator"
            aria-hidden="true"
          />

          </div>

          <div className={styles.navFooterSection}>
            <div
              className={styles.sectionSeparator}
              role="separator"
              aria-hidden="true"
            />
            <div className={styles.sectionBlock}>
              <Accordion
                collapsible
                multiple
                className={styles.navAccordion}
              >
                <NavItem
                  label="Settings"
                  icon={<SettingsRegular />}
                  onClick={() => handleNavItemClick("/admin", "admin-settings")}
                  disabled={!checkAdmin()}
                  isExpanded={isExpanded}
                  itemId="admin-settings"
                  href="/admin"
                  clickedItem={clickedItem}
                  styles={styles}
                />
                {/* Admin-only Basecamp Polaris destinations, gated identically to Settings. */}
                <NavItem
                  label="User Management"
                  icon={<PeopleTeamRegular />}
                  onClick={() => {
                    setClickedItem("user-management");
                    setTimeout(() => setClickedItem(null), 200);
                    openExternalNavLink(
                      buildBasecampBffLoginUrl(basecampUrl, "/users"),
                      "_blank",
                      "user-management"
                    );
                  }}
                  disabled={!checkAdmin()}
                  isExpanded={isExpanded}
                  itemId="user-management"
                  clickedItem={clickedItem}
                  styles={styles}
                />
                <NavItem
                  label="Workspaces"
                  icon={<BuildingRegular />}
                  onClick={() => {
                    setClickedItem("workspaces");
                    setTimeout(() => setClickedItem(null), 200);
                    openExternalNavLink(
                      buildBasecampBffLoginUrl(basecampUrl, "/workspaces"),
                      "_blank",
                      "workspaces"
                    );
                  }}
                  disabled={!checkAdmin()}
                  isExpanded={isExpanded}
                  itemId="workspaces"
                  clickedItem={clickedItem}
                  styles={styles}
                />
                <NavItem
                  label="Knowledge Base"
                  icon={<BookOpenRegular />}
                  onClick={() => {
                    setClickedItem("help-support");
                    setTimeout(() => setClickedItem(null), 200);
                    openExternalNavLink(guidesUrl, "_blank", "knowledge-base");
                  }}
                  isExpanded={isExpanded}
                  itemId="help-support"
                  clickedItem={clickedItem}
                  styles={styles}
                />
              </Accordion>
            </div>
          </div>
        </div>
      </div>
    </Drawer>
  );
};

export default SideNav;
